/**
 * Thinking Steps Service
 * 
 * Provides pre-written thinking steps that show what the AI is "doing"
 * while waiting for the first token.
 * 
 * Features:
 * - Large pools of contextual steps (30-50 per activity)
 * - Randomized order for variety
 * - No API calls - instant response
 */

import { ActivityContext } from '../store/thinkingLoaderStore';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger();

/**
 * Get randomized thinking steps for an activity
 */
export function getThinkingSteps(activity: string): { text: string }[] {
  log.log('[ThinkingSteps] Getting steps for:', activity);
  
  const stepsPool = THINKING_STEPS_POOL[activity] || THINKING_STEPS_POOL['responding'];
  
  // Shuffle and return steps for variety
  const shuffled = shuffleArray([...stepsPool]);
  
  return shuffled.map(text => ({ text }));
}

/**
 * The fixed lead phrase shown first for an activity (before the pool starts
 * cycling). Orchestration stages get a stage-specific lead ("Researching",
 * "Planning", "Continuing"); everything else defaults to "Thinking".
 */
const ACTIVITY_LEAD: Record<string, string> = {
  research: 'Researching',
  planning: 'Planning',
  continuing: 'Continuing',
  reviewing: 'Reviewing',
};

export function getLeadPhrase(activity: string | null | undefined): string {
  return (activity && ACTIVITY_LEAD[activity]) || 'Thinking';
}

/**
 * Detect activity type from message content or tool mode
 */
export function detectActivity(props: {
  toolMode?: string;
  content?: any;
  context?: any;
}): { activity: string; context?: ActivityContext } {
  
  const { toolMode, content, context } = props;
  
  // 1. Check toolMode first (most reliable)
  if (toolMode) {
    // Parse tool name to extract action (e.g., 'doc_write' -> 'write')
    let action = toolMode;
    if (toolMode.startsWith('doc_')) {
      // Extract the action part: 'doc_write' -> 'write'
      action = toolMode.replace(/^doc_/, '').replace(/_v\d+$/, '').replace(/_/g, '');
    }
    
    const activityMap: Record<string, string> = {
      'write': 'writing',
      'read': 'reading',
      'remove': 'removing',
      'image': 'image',
      'generateimage': 'image', // for doc_generate_image
      'review': 'review',
      'search': 'searching'
    };
    
    const activity = activityMap[action] || 'responding';
    const docName = context?.windowTitle || context?.entityName;
    
    console.log(`🎯 [ACTIVITY_DETECTION] toolMode: ${toolMode}, action: ${action}, activity: ${activity}`);
    
    return {
      activity,
      context: {
        toolName: toolMode,
        documentName: docName,
        operation: action
      }
    };
  }
  
  // 2. Parse message content
  const text = getMessageText(content);
  if (!text) {
    return { activity: 'responding' };
  }
  
  const lowerText = text.toLowerCase();
  const docName = context?.windowTitle || context?.entityName;
  
  // Check for specific activities
  if ((lowerText.includes('read') || lowerText.includes('show')) && docName) {
    return {
      activity: 'reading',
      context: {
        toolName: 'doc_read',
        documentName: docName
      }
    };
  }
  
  if (lowerText.includes('write') || lowerText.includes('add') || lowerText.includes('insert')) {
    return {
      activity: 'writing',
      context: {
        toolName: 'doc_write',
        documentName: docName
      }
    };
  }
  
  if (lowerText.includes('remove') || lowerText.includes('delete')) {
    return {
      activity: 'removing',
      context: {
        toolName: 'doc_remove',
        documentName: docName
      }
    };
  }
  
  if (lowerText.includes('generate') && lowerText.includes('image')) {
    return {
      activity: 'image',
      context: {
        toolName: 'doc_generate_image',
        imagePrompt: text
      }
    };
  }
  
  if (lowerText.includes('review') || lowerText.includes('analyze')) {
    return {
      activity: 'review',
      context: {
        operation: 'review',
        documentName: docName
      }
    };
  }
  
  if (lowerText.includes('search') || lowerText.includes('find') || lowerText.includes('look up')) {
    return {
      activity: 'searching',
      context: {
        operation: 'search'
      }
    };
  }
  
  // Default: responding
  return { activity: 'responding' };
}

/**
 * Helper to extract text from various content formats
 */
function getMessageText(content: any): string {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (content.text) return content.text;
  if (Array.isArray(content) && content.length > 0) {
    const textItem = content.find(c => c.type === 'text');
    return textItem?.text || '';
  }
  return '';
}

/**
 * Shuffle array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Large pools of thinking steps for each activity type
 * Each step is 4-10 words, vague but relevant
 */
const THINKING_STEPS_POOL: Record<string, string[]> = {
  'responding': [
    'Reading through your request carefully',
    'Understanding what you need',
    'Analyzing the context provided',
    'Thinking through the best approach',
    'Considering different solutions',
    'Formulating a clear response',
    'Organizing my thoughts',
    'Checking my understanding',
    'Preparing a detailed answer',
    'Reviewing the requirements',
    'Processing the information',
    'Gathering relevant details',
    'Structuring the response',
    'Verifying accuracy',
    'Double-checking the context',
    'Crafting a helpful reply',
    'Analyzing your question',
    'Thinking about the details',
    'Preparing my response',
    'Considering the best way forward',
    'Reviewing related information',
    'Organizing the answer',
    'Checking for clarity',
    'Finalizing my thoughts',
    'Ensuring completeness',
    'Validating my approach',
    'Refining the response',
    'Putting it all together',
    'Making sure this makes sense',
    'Preparing the final answer',
    'One moment while I think',
    'Almost ready with the response',
    'Just finishing up my thoughts',
    'Getting everything organized',
    'Making final adjustments'
  ],
  
  'reading': [
    'Opening the document',
    'Scanning through the content',
    'Reading the document carefully',
    'Processing the information',
    'Analyzing the structure',
    'Understanding the content',
    'Extracting key points',
    'Reviewing the details',
    'Parsing through the text',
    'Examining the document',
    'Taking note of important parts',
    'Reading through section by section',
    'Understanding the main ideas',
    'Identifying key information',
    'Processing each section',
    'Analyzing the content',
    'Gathering relevant details',
    'Making sense of the information',
    'Reviewing the structure',
    'Understanding the context',
    'Extracting important details',
    'Reading thoroughly',
    'Processing the document',
    'Analyzing what I find',
    'Taking in the information',
    'Understanding the full context',
    'Reviewing each part carefully',
    'Scanning for key points',
    'Processing what I read',
    'Analyzing the document structure',
    'Understanding the organization',
    'Extracting the main points',
    'Reading through methodically',
    'Processing all the content',
    'Reviewing everything carefully',
    'Understanding the full picture',
    'Analyzing the information provided',
    'Taking note of details',
    'Processing the text',
    'Reading and understanding'
  ],
  
  'writing': [
    'Planning the content',
    'Organizing my thoughts',
    'Structuring the text',
    'Drafting the initial content',
    'Thinking about the structure',
    'Preparing the text',
    'Composing the content',
    'Crafting the message',
    'Organizing the information',
    'Planning the structure',
    'Drafting the text',
    'Refining the language',
    'Polishing the content',
    'Structuring the response',
    'Preparing the draft',
    'Composing the text carefully',
    'Organizing the flow',
    'Creating the content',
    'Drafting the message',
    'Planning how to structure this',
    'Thinking about the best flow',
    'Preparing the writing',
    'Crafting the language',
    'Organizing the ideas',
    'Structuring the document',
    'Composing thoughtfully',
    'Drafting the initial version',
    'Refining the structure',
    'Polishing the text',
    'Creating the draft',
    'Organizing the content',
    'Planning the layout',
    'Preparing the text carefully',
    'Composing the response',
    'Structuring the information',
    'Drafting the content',
    'Refining the message',
    'Polishing the language',
    'Creating the text',
    'Finalizing the content structure'
  ],
  
  'removing': [
    'Locating the target content',
    'Finding the section to remove',
    'Identifying what to delete',
    'Scanning for the content',
    'Locating the text',
    'Finding the right section',
    'Identifying the target',
    'Searching for the content',
    'Locating what needs to go',
    'Finding the specific part',
    'Identifying the section',
    'Scanning through to find it',
    'Locating the exact content',
    'Finding the target text',
    'Identifying what to remove',
    'Searching for the section',
    'Locating the specific content',
    'Finding what needs removal',
    'Identifying the right part',
    'Scanning for the target',
    'Preparing to remove',
    'Verifying the selection',
    'Confirming what to delete',
    'Checking the target',
    'Validating the selection',
    'Ensuring accuracy',
    'Double-checking the content',
    'Verifying the right section',
    'Confirming the removal',
    'Checking the selection',
    'Removing the content',
    'Deleting the section',
    'Clearing the text',
    'Removing the selected part',
    'Updating the document',
    'Finalizing the removal',
    'Cleaning up the content',
    'Completing the deletion',
    'Finishing the removal',
    'Updating the structure'
  ],
  
  'image': [
    'Analyzing the image prompt',
    'Understanding the visual requirements',
    'Processing the description',
    'Planning the image composition',
    'Interpreting the prompt',
    'Considering the visual elements',
    'Thinking about the style',
    'Planning the artwork',
    'Analyzing what to create',
    'Understanding the vision',
    'Processing the requirements',
    'Considering the details',
    'Planning the visual',
    'Thinking about composition',
    'Analyzing the prompt carefully',
    'Understanding the aesthetic',
    'Processing the creative direction',
    'Planning the image',
    'Considering the style',
    'Thinking about the details',
    'Generating the image',
    'Creating the artwork',
    'Processing the visual',
    'Generating the content',
    'Creating the image',
    'Processing the generation',
    'Rendering the artwork',
    'Creating the visual',
    'Generating the result',
    'Processing the image',
    'Preparing the artwork',
    'Finalizing the image',
    'Processing and optimizing',
    'Preparing the visual',
    'Getting the image ready',
    'Finalizing the artwork',
    'Preparing to insert',
    'Getting ready to add it',
    'Almost done with generation',
    'Finishing up the image'
  ],
  
  'review': [
    'Reading through the document',
    'Analyzing the content',
    'Reviewing the structure',
    'Examining the organization',
    'Checking the flow',
    'Analyzing the writing',
    'Reviewing the details',
    'Examining the content carefully',
    'Checking for consistency',
    'Analyzing the structure',
    'Reviewing the organization',
    'Examining the layout',
    'Checking the content',
    'Analyzing the approach',
    'Reviewing each section',
    'Examining the details',
    'Checking the quality',
    'Analyzing the writing style',
    'Reviewing the language',
    'Examining the flow',
    'Checking for clarity',
    'Analyzing the coherence',
    'Reviewing the logic',
    'Examining the arguments',
    'Checking the reasoning',
    'Analyzing the evidence',
    'Reviewing the examples',
    'Examining the supporting details',
    'Checking the accuracy',
    'Analyzing the facts',
    'Reviewing the information',
    'Examining the data',
    'Checking the sources',
    'Analyzing the references',
    'Reviewing thoroughly',
    'Examining everything carefully',
    'Checking all aspects',
    'Preparing detailed feedback',
    'Organizing my notes',
    'Finalizing the review'
  ],
  
  // Orchestration: context-gathering stage. Lead phrase "Researching" is shown
  // first (see getLeadPhrase); these cycle after it.
  'research': [
    'Gathering context',
    'Digging into the details',
    'Pulling together relevant information',
    'Scanning your workspace',
    'Reviewing related entities',
    'Checking memories and notes',
    'Consulting relevant skills',
    'Looking through past conversations',
    'Searching for the facts',
    'Cross-checking sources',
    'Collecting what matters',
    'Reviewing connected data',
    'Verifying the details',
    'Mapping out what is available',
    'Tracking down specifics',
    'Assembling the background',
    'Reading the relevant material',
    'Lining up the key facts',
    'Surveying the landscape',
    'Getting up to speed'
  ],

  // Orchestration: assessment-driven retry. Lead phrase "Continuing" is shown
  // first; these cycle after it while execution resumes where it left off.
  'continuing': [
    'Picking up where I left off',
    'Resuming the task',
    'Reviewing what is done so far',
    'Finishing the remaining steps',
    'Carrying on from here',
    'Wrapping up the open items',
    'Closing the gaps',
    'Continuing the work',
    'Completing what remains',
    'Tying up loose ends',
    'Pushing the task to completion',
    'Working through the rest',
    'Building on the earlier work',
    'Getting the last pieces done',
    'Following through on the plan',
    'Handling what is still open',
    'Driving toward the finish',
    'Picking the next step back up',
    'Finalizing the remaining work',
    'Seeing the task through'
  ],

  // Orchestration: planning stage. Lead phrase "Planning" is shown first.
  'planning': [
    'Mapping out the steps',
    'Outlining the approach',
    'Breaking the task into steps',
    'Sequencing the work',
    'Structuring the plan',
    'Deciding what to do first',
    'Sketching the roadmap',
    'Prioritizing the steps',
    'Defining the done-criteria',
    'Laying out the checklist',
    'Organizing the work',
    'Charting the path forward',
    'Working out the details',
    'Lining up the tasks',
    'Drafting the plan',
    'Refining the approach',
    'Setting up the steps',
    'Figuring out the order',
    'Thinking through the sequence',
    'Shaping the game plan'
  ],
  
  'searching': [
    'Searching for information',
    'Looking up relevant details',
    'Finding the right sources',
    'Searching the web',
    'Looking for answers',
    'Finding relevant information',
    'Searching for data',
    'Looking up the details',
    'Finding the facts',
    'Searching online',
    'Looking for reliable sources',
    'Finding accurate information',
    'Searching thoroughly',
    'Looking through results',
    'Finding what you need',
    'Analyzing search results',
    'Reviewing what I found',
    'Examining the sources',
    'Checking the information',
    'Analyzing the data',
    'Reviewing the results',
    'Examining the findings',
    'Checking the sources',
    'Analyzing the content',
    'Reviewing the information',
    'Extracting key insights',
    'Gathering relevant details',
    'Collecting important information',
    'Extracting the facts',
    'Gathering the data',
    'Collecting useful details',
    'Extracting what matters',
    'Gathering everything relevant',
    'Compiling the findings',
    'Organizing the information',
    'Putting it all together',
    'Summarizing the results',
    'Organizing the data',
    'Preparing the summary',
    'Finalizing the findings'
  ]
};
